import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowRight, MessageCircle, Phone, CheckCircle2, Clock, Star, Tag, Sparkles } from 'lucide-react';
import { DESTINATIONS, PACKAGES, COMPANY, HIMACHAL_TOURS } from '../mock';
import { Button } from '../components/ui/button';
import { PackageCard } from '../components/sections/Packages';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import HeroSection from '../components/sections/HeroSection';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { getVideoReviews } from '../constants/videoReviews';

export function DestinationsPage() {
  return (
    <div>
      <Helmet>
        <title>All Destinations | Mountains Chain Travel</title>
        <meta name="description" content="Explore our curated destinations across North India and Internationally. Experience Himachal, Kashmir, Ladakh, Uttarakhand, and more with Mountains Chain Travel." />
        <meta property="og:title" content="All Destinations | Mountains Chain Travel" />
        <meta property="og:description" content="Explore our curated destinations across North India and Internationally. Experience Himachal, Kashmir, Ladakh, Uttarakhand, and more with Mountains Chain Travel." />
      </Helmet>

      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src={DESTINATIONS[0].image} alt="Destinations" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40">
              <span className="tracking-wide">Curated Journeys Across India</span>
            </div>
            <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl">
              <span className="text-brand-orange">All</span>
              <br />
              <span>Destinations</span>
            </h1>
            <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg">
              From Himalayan peaks to tropical beaches - discover India's most breathtaking destinations with hand-crafted journeys.
            </p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Explore</span>
              <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                Himachal · Kashmir · Ladakh · Uttarakhand · Kerala · Rajasthan
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards />
      <CustomerVideoReviews />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {DESTINATIONS.map(d => (
            <Link key={d.slug} to={`/destinations/${d.slug}`} className="group rounded-2xl overflow-hidden img-zoom-wrap card-hover relative h-[360px] block">
              <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/40 to-transparent" />
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <h3 className="font-poppins font-bold text-[26px]">{d.name}</h3>
                <p className="text-white/80 text-[14px] mt-1">{d.tagline}</p>
                <div className="mt-4 flex items-center gap-3 text-[12.5px] text-white/75">
                  <span>{d.placesCount} places</span><span className="opacity-50">•</span><span>From ₹{d.startingPrice.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CTABanner />
      <EnquiryForm />
    </div>
  );
}

export function DestinationDetailPage() {
  const { slug } = useParams();
  const dest = DESTINATIONS.find(d => d.slug === slug);
  if (!dest) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-brand-blue">Destination not found. <Link to="/destinations" className="text-brand-orange underline">Back to destinations</Link></div>;
  const pkgs = PACKAGES.filter(p => p.destination === slug);
  return (
    <div>
      <Helmet>
        <title>{dest.name} Tour Packages | Mountains Chain Travel</title>
        <meta name="description" content={`Explore the best ${dest.name} tour packages with Mountains Chain Travel. ${dest.tagline} Tailored for your budget and style.`} />
        <meta property="og:title" content={`${dest.name} Tour Packages | Mountains Chain Travel`} />
        <meta property="og:description" content={`Explore the best ${dest.name} tour packages with Mountains Chain Travel. ${dest.tagline} Tailored for your budget and style.`} />
      </Helmet>
      <HeroSection slug={slug} name={dest.name} startingPrice={dest.startingPrice} />
      <ReviewCards destinationKey={slug} />
      <CustomerVideoReviews reviews={getVideoReviews(slug)} />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-poppins text-[28px] text-brand-blue mb-3 flex items-center gap-2"><Sparkles size={22} className="text-brand-orange"/>About {dest.name}</h2>
            <p className="text-brand-blue/70 text-[15px] leading-relaxed">From quiet pine valleys and ancient monasteries to lakes that mirror the sky, {dest.name} is where we have built deep local roots since 2015. Our hand-picked stays, verified drivers and seasoned guides ensure every moment is comfortable, authentic and unforgettable.</p>
            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-3">Top places we cover</h3>
            <div className="flex flex-wrap gap-2">
              {dest.highlights.map(h => (
                <span key={h} className="px-3 py-1.5 bg-brand-grey rounded-full text-[13px] text-brand-blue font-medium flex items-center gap-1.5"><MapPin size={12} className="text-brand-orange" />{h}</span>
              ))}
            </div>
            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-4">Why book {dest.name} with us</h3>
            <ul className="space-y-2.5">
              {['Local guides who grew up here','Verified, inspected hotels & homestays','Permits & paperwork handled for you','24x7 on-trip emergency support','Customizable to your budget & pace'].map((x,i) => (
                <li key={i} className="flex items-start gap-3 text-brand-blue/80 text-[14.5px]"><CheckCircle2 size={18} className="text-brand-orange mt-0.5 shrink-0" />{x}</li>
              ))}
            </ul>
          </div>
          <aside className="bg-brand-grey rounded-2xl p-6 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold uppercase tracking-wider mb-3"><Clock size={14}/>Plan Your Trip</div>
            <p className="text-brand-blue/70 text-[13.5px] mb-4">Get a custom itinerary in minutes. Free, no obligation - a real travel designer replies on WhatsApp.</p>
            <div className="flex flex-col gap-2.5 mt-2">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a trip to ' + dest.name + ' and would love a customized itinerary.\n\nPlease share:\n- Available tour packages\n- Pricing & hotel options\n- Best time to visit\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>Get Custom Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Customize</Button>
              </a>
              <Link to={`/domestic/${slug}`} className="inline-flex items-center justify-center gap-1 text-[12.5px] text-brand-blue/60 hover:text-brand-orange mt-1 transition-colors">
                <ArrowRight size={12}/> View domestic packages
              </Link>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-greyDark space-y-2 text-[12.5px] text-brand-blue/75">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Pay just 20% to book</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>0% EMI available</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>24x7 on-trip support</div>
            </div>
          </aside>
        </div>

        <div className="mt-16">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <h2 className="font-poppins text-[28px] lg:text-[34px] text-brand-blue">Top packages in {dest.name}</h2>
            {dest.slug === 'himachal-pradesh' ? (
              <Link to="/himachal-tours" className="text-brand-orange font-semibold flex items-center gap-1">View all 9 Himachal tours <ArrowRight size={15}/></Link>
            ) : (
              <Link to="/packages" className="text-brand-orange font-semibold flex items-center gap-1">All packages <ArrowRight size={15}/></Link>
            )}
          </div>

          {dest.slug === 'himachal-pradesh' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {HIMACHAL_TOURS.map((t, i) => {
                const discount = Math.round(((t.oldPrice - t.price) / t.oldPrice) * 100);
                return (
                  <Link key={t.slug} to={`/himachal-tours/${t.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-brand-greyDark shadow-card card-hover">
                    <div className="relative aspect-[4/3] img-zoom-wrap overflow-hidden">
                      <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-[11px] font-bold text-brand-blue">
                        <Clock size={11} className="text-brand-orange"/>{t.nights}N / {t.days}D
                      </div>
                      {discount > 0 && (
                        <div className="absolute top-3 right-3 bg-brand-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1"><Tag size={11}/>{discount}% OFF</div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-poppins font-bold text-brand-blue text-[17px] leading-snug group-hover:text-brand-orange transition-colors line-clamp-2">{t.name}</h3>
                      <p className="text-brand-blue/65 text-[13px] mt-1.5 line-clamp-2">{t.tagline}</p>
                      <div className="flex items-center gap-3 mt-3 text-[12px] text-brand-blue/60">
                        <span className="flex items-center gap-1"><Star size={12} className="fill-brand-orange text-brand-orange"/>{t.rating} ({t.reviews})</span>
                        <span className="flex items-center gap-1"><MapPin size={12}/>{t.places.length} places</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-brand-grey flex items-end justify-between">
                        <div>
                          <div className="text-[10.5px] text-brand-blue/50 line-through">₹{t.oldPrice.toLocaleString('en-IN')}</div>
                          <div className="font-poppins font-bold text-brand-blue text-[19px] leading-none">₹{t.price.toLocaleString('en-IN')}<span className="text-[11px] text-brand-blue/55 font-normal">/person</span></div>
                        </div>
                        <div className="text-brand-orange font-semibold text-[12.5px] flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight size={13}/></div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : pkgs.length === 0 ? (
            <div className="text-brand-blue/60 bg-brand-grey rounded-xl p-8 text-center">More packages coming soon - contact us for a custom plan.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pkgs.map(p => <PackageCard key={p.id} pkg={p} />)}
            </div>
          )}
        </div>
      </section>
      <EnquiryForm />
    </div>
  );
}

export function PageHero({ title, sub, image }) {
  return (
    <section className="relative h-[44vh] min-h-[300px] max-h-[420px] w-full overflow-hidden">
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/80 via-brand-blue/55 to-brand-blue/30" />
      <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center text-white">
        <div className="text-brand-orange text-[12px] font-semibold tracking-[0.2em] uppercase mb-2 animate-fade-up">Mountains Chain Travel</div>
        <h1 className="font-poppins font-extrabold text-[40px] lg:text-[58px] leading-[1.05] animate-fade-up">{title}</h1>
        {sub && <p className="text-white/85 mt-3 text-[16px] lg:text-[18px] max-w-2xl animate-fade-up">{sub}</p>}
      </div>
    </section>
  );
}
